import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Trash2,
  FileSpreadsheet,
  Loader2,
  Download,
  Calendar as CalendarIcon,
  BarChart3,
} from "lucide-react";

const fmt = (n) => {
  if (n === null || n === undefined) return "";
  const v = Math.round(n);
  if (v === 0) return "-";
  return new Intl.NumberFormat("vi-VN").format(v);
};

const fmtChenhLech = (v) => {
  if (!v || v === 0) return "-";
  const s = new Intl.NumberFormat("vi-VN").format(Math.abs(v));
  return v < 0 ? `(${s})` : s;
};

const formatDateVN = (d) => {
  if (!d || d.length < 10) return d;
  return `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`;
};

const FIXED_TAI_KHOAN = ["KHU KARINOX", "KHU PIPPY", "KHU CF ARENA"];

const FILTER_PRESETS = [
  { key: "1d", label: "Hôm nay" },
  { key: "3d", label: "3 ngày" },
  { key: "7d", label: "7 ngày" },
  { key: "month", label: "Tháng này" },
  { key: "custom", label: "Tuỳ chọn" },
];

// Màu chênh lệch theo giá trị
const chenhLechColor = (v) => {
  if (!v || v === 0) return "text-slate-500";
  return v < 0 ? "text-red-400" : "text-emerald-400";
};

export default function DoanhThuNgayTab() {
  const today = new Date().toISOString().slice(0, 10);
  const [filterPreset, setFilterPreset] = useState("7d");
  const [customStart, setCustomStart] = useState(today);
  const [customEnd, setCustomEnd] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newEntry, setNewEntry] = useState({
    date: today,
    tai_khoan: FIXED_TAI_KHOAN[0],
    doanh_thu_so: 0,
    doanh_thu_kiot: 0,
    doanh_thu_chenh_lech: 0,
    tien_mat_so: 0,
    tien_mat_kiot: 0,
    tien_mat_chenh_lech: 0,
    vnpay: 0,
    momo_diem: 0,
    grab: 0,
    quet_the: 0,
    ck_so: 0,
    ck_kiot: 0,
    ck_chenh_lech: 0,
    ghi_chu_hd: "",
    ghi_chu: "",
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterPreset === "custom") {
        params.set("filter", "custom");
        params.set("start", customStart);
        params.set("end", customEnd);
      } else {
        params.set("filter", filterPreset);
      }
      const { data: d } = await api.get(`/doanh-thu-ngay?${params.toString()}`);
      setData(d);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi tải dữ liệu");
    } finally {
      setLoading(false);
    }
  }, [filterPreset, customStart, customEnd]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddEntry = async () => {
    if (!FIXED_TAI_KHOAN.includes(newEntry.tai_khoan)) {
      toast.error("Tài khoản không hợp lệ");
      return;
    }
    try {
      const intFields = [
        "doanh_thu_so", "doanh_thu_kiot", "doanh_thu_chenh_lech",
        "tien_mat_so", "tien_mat_kiot", "tien_mat_chenh_lech",
        "vnpay", "momo_diem", "grab", "quet_the",
        "ck_so", "ck_kiot", "ck_chenh_lech",
      ];
      const payload = { ...newEntry };
      intFields.forEach((f) => { payload[f] = parseInt(payload[f]) || 0; });
      await api.post("/doanh-thu-ngay/entries", payload);
      toast.success("Đã thêm dòng Doanh thu ngày");
      setShowAddDialog(false);
      setNewEntry({
        date: today,
        tai_khoan: FIXED_TAI_KHOAN[0],
        doanh_thu_so: 0, doanh_thu_kiot: 0, doanh_thu_chenh_lech: 0,
        tien_mat_so: 0, tien_mat_kiot: 0, tien_mat_chenh_lech: 0,
        vnpay: 0, momo_diem: 0, grab: 0, quet_the: 0,
        ck_so: 0, ck_kiot: 0, ck_chenh_lech: 0,
        ghi_chu_hd: "", ghi_chu: "",
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi thêm");
    }
  };

  const handleDeleteEntry = async (id, tk, date) => {
    if (!window.confirm(`Xoá dòng ${tk} ngày ${formatDateVN(date)}?`)) return;
    try {
      await api.delete(`/doanh-thu-ngay/entries/${id}`);
      toast.success("Đã xoá");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi xoá");
    }
  };

  const handleExportExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (filterPreset === "custom") {
        params.set("filter", "custom");
        params.set("start", customStart);
        params.set("end", customEnd);
      } else {
        params.set("filter", filterPreset);
      }
      const resp = await api.get(`/doanh-thu-ngay/export?${params.toString()}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `doanh_thu_ngay.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Đã xuất Excel");
    } catch (err) {
      toast.error("Lỗi xuất file");
    }
  };

  const groups = data?.groups || [];
  const grand = data?.grand_total || {};

  return (
    <div className="space-y-3" data-testid="doanh-thu-ngay-tab">
      {/* Banner: import keep as-is */}
      <div
        className="bg-indigo-500/10 border border-indigo-500/30 rounded-lg px-3 py-2 text-[10.5px] text-indigo-300"
        data-testid="dtn-import-policy-banner"
      >
        ⓘ Mọi số liệu trong bảng dưới đây được giữ <strong>nguyên 100%</strong> từ file đã import (Discord auto / Tài liệu manual).
        Hệ thống <strong>KHÔNG tự sửa, KHÔNG tự tính chênh lệch</strong>. Cột "Chênh lệch" được OCR đúng giá trị trong file gốc.
        Nếu cần đối chiếu với KiotViet → dùng tab <strong>"Đối chiếu KV"</strong> riêng.
      </div>

      {/* Header + Filter */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm font-bold text-slate-200 flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-indigo-400" /> Doanh thu ngày
        </h2>
        <div className="flex items-center gap-2 flex-wrap">
          {FILTER_PRESETS.map((p) => (
            <button
              key={p.key}
              onClick={() => setFilterPreset(p.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                filterPreset === p.key
                  ? "bg-indigo-500 text-white"
                  : "bg-[#1e293b] text-slate-400 hover:text-slate-200 hover:bg-[#334155]"
              }`}
              data-testid={`dtn-filter-${p.key}`}
            >
              {p.label}
            </button>
          ))}
          {filterPreset === "custom" && (
            <>
              <Input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="h-8 bg-[#0f172a] border-[#334155] text-slate-200 text-xs w-[130px]"
                data-testid="dtn-custom-start"
              />
              <span className="text-slate-500 text-xs">→</span>
              <Input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="h-8 bg-[#0f172a] border-[#334155] text-slate-200 text-xs w-[130px]"
                data-testid="dtn-custom-end"
              />
            </>
          )}
          <Button
            onClick={() => setShowAddDialog(true)}
            size="sm"
            className="h-8 bg-emerald-500 hover:bg-emerald-400 text-white text-xs"
            data-testid="dtn-add-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Thêm dòng
          </Button>
          <Button
            onClick={handleExportExcel}
            variant="outline"
            size="sm"
            className="h-8 border-[#334155] text-slate-300 hover:bg-[#334155] text-xs"
            data-testid="dtn-export-btn"
          >
            <Download className="w-3.5 h-3.5 mr-1" /> Xuất Excel
          </Button>
        </div>
      </div>

      {/* Summary KPI cards */}
      {!loading && data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dtn-summary">
          <KpiCard
            color="blue"
            title="Doanh thu (sổ kết ca)"
            value={grand.doanh_thu_so}
            sub={`KiotViet ${fmt(grand.doanh_thu_kiot)}đ`}
          />
          <KpiCard
            color="orange"
            title="Tiền mặt (sổ kết ca)"
            value={grand.tien_mat_so}
            sub={`KiotViet ${fmt(grand.tien_mat_kiot)}đ`}
          />
          <KpiCard
            color="green"
            title="Chuyển khoản (sổ)"
            value={grand.ck_so}
            sub={`KiotViet ${fmt(grand.ck_kiot)}đ`}
          />
          <KpiCard
            color={grand.doanh_thu_chenh_lech === 0 ? "emerald" : "amber"}
            title="Tổng lệch Doanh thu"
            value={grand.doanh_thu_chenh_lech}
            chenhLech
            sub={
              grand.doanh_thu_chenh_lech === 0
                ? "Khớp tuyệt đối"
                : grand.doanh_thu_chenh_lech > 0
                ? "Sổ > KiotViet"
                : "Sổ < KiotViet"
            }
          />
        </div>
      )}

      {loading ? (
        <Loader2 className="w-5 h-5 text-indigo-400 animate-spin mx-auto mt-8" />
      ) : groups.length === 0 ? (
        <div className="text-center py-16 bg-[#1e293b] border border-[#334155] rounded-xl">
          <FileSpreadsheet className="w-10 h-10 text-slate-600 mx-auto mb-2" />
          <p className="text-slate-400 text-sm">Chưa có dữ liệu Doanh thu ngày trong khoảng thời gian này</p>
          <p className="text-slate-600 text-xs mt-1">
            Upload ảnh bảng đối chiếu qua Discord #ke-toan hoặc bấm "Thêm dòng" để nhập tay
          </p>
        </div>
      ) : (
        <div className="space-y-5" data-testid="dtn-groups">
          {groups.map((g) => (
            <DateGroup key={g.date} group={g} onDelete={handleDeleteEntry} />
          ))}
        </div>
      )}

      {/* Add entry dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-2xl" data-testid="dtn-add-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#F8FAFC] text-base">Thêm dòng Doanh thu ngày</DialogTitle>
            <DialogDescription className="text-slate-500 text-xs">
              Nhập 1 dòng cho 1 tài khoản (khu) trong 1 ngày. Chênh lệch sẽ tự tính.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-slate-400 mb-1 block">Ngày</label>
                <Input
                  type="date"
                  value={newEntry.date}
                  onChange={(e) => setNewEntry((p) => ({ ...p, date: e.target.value }))}
                  className="h-9 bg-[#0f172a] border-[#334155] text-slate-200 text-xs"
                  data-testid="dtn-add-date"
                />
              </div>
              <div>
                <label className="text-[11px] text-slate-400 mb-1 block">Tài khoản</label>
                <Select
                  value={newEntry.tai_khoan}
                  onValueChange={(v) => setNewEntry((p) => ({ ...p, tai_khoan: v }))}
                >
                  <SelectTrigger
                    className="h-9 bg-[#0f172a] border-[#334155] text-slate-200 text-xs"
                    data-testid="dtn-add-tai-khoan"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1e293b] border-[#334155]">
                    {FIXED_TAI_KHOAN.map((tk) => (
                      <SelectItem key={tk} value={tk} className="text-slate-200 text-xs">
                        {tk}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-2 space-y-2">
              <p className="text-[10px] text-blue-300 uppercase font-bold">Doanh thu</p>
              <div className="grid grid-cols-3 gap-2">
                <FieldInt label="Ghi sổ kết ca" value={newEntry.doanh_thu_so}
                  onChange={(v) => setNewEntry((p) => ({ ...p, doanh_thu_so: v }))} testid="dtn-add-dt-so" />
                <FieldInt label="Kiot Việt" value={newEntry.doanh_thu_kiot}
                  onChange={(v) => setNewEntry((p) => ({ ...p, doanh_thu_kiot: v }))} testid="dtn-add-dt-kv" />
                <FieldInt label="Chênh lệch (từ file)" value={newEntry.doanh_thu_chenh_lech}
                  onChange={(v) => setNewEntry((p) => ({ ...p, doanh_thu_chenh_lech: v }))} testid="dtn-add-dt-cl" />
              </div>
              <div className="text-[10px] text-blue-300/70 italic">
                ⓘ Nhập Chênh lệch đúng như trong file gốc — hệ thống KHÔNG tự tính.
              </div>
            </div>

            <div className="bg-orange-500/5 border border-orange-500/20 rounded-lg p-2 space-y-2">
              <p className="text-[10px] text-orange-300 uppercase font-bold">Tiền mặt</p>
              <div className="grid grid-cols-3 gap-2">
                <FieldInt label="Ghi sổ kết ca" value={newEntry.tien_mat_so}
                  onChange={(v) => setNewEntry((p) => ({ ...p, tien_mat_so: v }))} testid="dtn-add-tm-so" />
                <FieldInt label="Kiot Việt" value={newEntry.tien_mat_kiot}
                  onChange={(v) => setNewEntry((p) => ({ ...p, tien_mat_kiot: v }))} testid="dtn-add-tm-kv" />
                <FieldInt label="Chênh lệch (từ file)" value={newEntry.tien_mat_chenh_lech}
                  onChange={(v) => setNewEntry((p) => ({ ...p, tien_mat_chenh_lech: v }))} testid="dtn-add-tm-cl" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <FieldInt label="VNPAY" value={newEntry.vnpay}
                onChange={(v) => setNewEntry((p) => ({ ...p, vnpay: v }))} testid="dtn-add-vnpay" />
              <FieldInt label="MOMO / Điểm TV" value={newEntry.momo_diem}
                onChange={(v) => setNewEntry((p) => ({ ...p, momo_diem: v }))} testid="dtn-add-momo" />
              <FieldInt label="GRAB" value={newEntry.grab}
                onChange={(v) => setNewEntry((p) => ({ ...p, grab: v }))} testid="dtn-add-grab" />
              <FieldInt label="Quẹt thẻ" value={newEntry.quet_the}
                onChange={(v) => setNewEntry((p) => ({ ...p, quet_the: v }))} testid="dtn-add-quet" />
            </div>

            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-2 space-y-2">
              <p className="text-[10px] text-emerald-300 uppercase font-bold">Chuyển khoản</p>
              <div className="grid grid-cols-3 gap-2">
                <FieldInt label="Ghi sổ kết ca" value={newEntry.ck_so}
                  onChange={(v) => setNewEntry((p) => ({ ...p, ck_so: v }))} testid="dtn-add-ck-so" />
                <FieldInt label="Kiot Việt" value={newEntry.ck_kiot}
                  onChange={(v) => setNewEntry((p) => ({ ...p, ck_kiot: v }))} testid="dtn-add-ck-kv" />
                <FieldInt label="Chênh lệch (từ file)" value={newEntry.ck_chenh_lech}
                  onChange={(v) => setNewEntry((p) => ({ ...p, ck_chenh_lech: v }))} testid="dtn-add-ck-cl" />
              </div>
            </div>

            <div>
              <label className="text-[11px] text-slate-400 mb-1 block">Ghi chú HD</label>
              <Input
                value={newEntry.ghi_chu_hd}
                onChange={(e) => setNewEntry((p) => ({ ...p, ghi_chu_hd: e.target.value }))}
                placeholder="VD: hd 183073 nhầm kênh bán ck sang tm"
                className="h-9 bg-[#0f172a] border-[#334155] text-slate-200 text-xs"
                data-testid="dtn-add-ghi-chu-hd"
              />
            </div>
            <div>
              <label className="text-[11px] text-slate-400 mb-1 block">Ghi chú</label>
              <Input
                value={newEntry.ghi_chu}
                onChange={(e) => setNewEntry((p) => ({ ...p, ghi_chu: e.target.value }))}
                placeholder="VD: tuyết âm 9k, trứng thiếu 3k"
                className="h-9 bg-[#0f172a] border-[#334155] text-slate-200 text-xs"
                data-testid="dtn-add-ghi-chu"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowAddDialog(false)}
              className="border-[#334155] text-slate-300 hover:bg-[#334155] h-8 text-xs"
            >
              Huỷ
            </Button>
            <Button
              onClick={handleAddEntry}
              className="bg-emerald-500 hover:bg-emerald-400 text-white h-8 text-xs"
              data-testid="dtn-add-submit"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Thêm dòng
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function KpiCard({ color, title, value, sub, chenhLech }) {
  const palette = {
    blue: "from-blue-600/20 to-blue-500/5 border-blue-500/20 text-blue-300",
    orange: "from-orange-600/20 to-orange-500/5 border-orange-500/20 text-orange-300",
    green: "from-emerald-600/20 to-emerald-500/5 border-emerald-500/20 text-emerald-300",
    emerald: "from-emerald-600/20 to-emerald-500/5 border-emerald-500/20 text-emerald-300",
    amber: "from-amber-600/20 to-amber-500/5 border-amber-500/20 text-amber-300",
  }[color] || "from-slate-700/20 to-slate-800/5 border-slate-600/20 text-slate-300";
  return (
    <div className={`bg-gradient-to-br ${palette} border rounded-xl p-3`}>
      <p className="text-[9px] uppercase font-bold mb-1">{title}</p>
      <p className={`text-sm font-bold ${chenhLech ? chenhLechColor(value) : ""}`}>
        {chenhLech && value > 0 ? "+" : ""}{fmt(value)}đ
      </p>
      {sub && <p className="text-[10px] text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}


function FieldInt({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] text-slate-500 mb-1 block">{label}</label>
      <Input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 bg-[#0f172a] border-[#334155] text-slate-200 text-xs"
        data-testid={testid}
      />
    </div>
  );
}


function DateGroup({ group, onDelete }) {
  const { date, entries, subtotal } = group;
  return (
    <div
      className="bg-[#1e293b] border border-[#334155] rounded-xl overflow-hidden"
      data-testid={`dtn-group-${date}`}
    >
      {/* Date header */}
      <div className="flex items-center justify-between px-4 py-2 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border-b border-[#334155]">
        <div className="flex items-center gap-2">
          <CalendarIcon className="w-4 h-4 text-indigo-400" />
          <h3 className="text-sm font-bold text-indigo-300">Ngày {formatDateVN(date)}</h3>
          <Badge className="bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 text-[10px]">
            {entries.length} dòng
          </Badge>
        </div>
      </div>

      {/* Table 17 cột */}
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]" data-testid={`dtn-table-${date}`}>
          <thead>
            <tr className="bg-[#0f172a] border-b border-[#334155]">
              <th className="text-left p-2 text-slate-400 font-semibold min-w-[86px]">NGÀY</th>
              <th className="text-left p-2 text-slate-400 font-semibold min-w-[110px]">TÀI KHOẢN</th>
              <th className="text-right p-2 text-blue-300 font-semibold min-w-[100px] bg-blue-500/5">DT (sổ)</th>
              <th className="text-right p-2 text-blue-300 font-semibold min-w-[100px] bg-blue-500/5">DT (KV)</th>
              <th className="text-right p-2 text-blue-300 font-semibold min-w-[80px] bg-blue-500/5">Chênh lệch</th>
              <th className="text-right p-2 text-orange-300 font-semibold min-w-[100px] bg-orange-500/5">TM (sổ)</th>
              <th className="text-right p-2 text-orange-300 font-semibold min-w-[100px] bg-orange-500/5">TM (KV)</th>
              <th className="text-right p-2 text-orange-300 font-semibold min-w-[80px] bg-orange-500/5">Chênh lệch</th>
              <th className="text-right p-2 text-slate-400 font-semibold min-w-[100px]">VNPAY</th>
              <th className="text-right p-2 text-slate-400 font-semibold min-w-[100px]">MOMO/TV</th>
              <th className="text-right p-2 text-slate-400 font-semibold min-w-[80px]">GRAB</th>
              <th className="text-right p-2 text-slate-400 font-semibold min-w-[90px]">Quẹt thẻ</th>
              <th className="text-right p-2 text-emerald-300 font-semibold min-w-[100px] bg-emerald-500/5">CK (sổ)</th>
              <th className="text-right p-2 text-emerald-300 font-semibold min-w-[100px] bg-emerald-500/5">CK (KV)</th>
              <th className="text-right p-2 text-emerald-300 font-semibold min-w-[80px] bg-emerald-500/5">Chênh lệch</th>
              <th className="text-left p-2 text-slate-400 font-semibold min-w-[160px]">Ghi chú HD</th>
              <th className="text-left p-2 text-slate-400 font-semibold min-w-[140px]">Ghi chú</th>
              <th className="w-[40px]"></th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.id} className="border-b border-[#334155]/50 hover:bg-[#0f172a]/50" data-testid={`dtn-entry-row-${e.id}`}>
                <td className="p-2 text-slate-300">{formatDateVN(e.date)}</td>
                <td className="p-2 text-slate-100 font-medium">{e.tai_khoan}</td>
                <td className="p-2 text-right text-slate-200 bg-blue-500/5">{fmt(e.doanh_thu_so)}</td>
                <td className="p-2 text-right text-slate-200 bg-blue-500/5">{fmt(e.doanh_thu_kiot)}</td>
                <td className={`p-2 text-right bg-blue-500/5 font-semibold ${chenhLechColor(e.doanh_thu_chenh_lech)}`}>
                  {fmtChenhLech(e.doanh_thu_chenh_lech)}
                </td>
                <td className="p-2 text-right text-slate-200 bg-orange-500/5">{fmt(e.tien_mat_so)}</td>
                <td className="p-2 text-right text-slate-200 bg-orange-500/5">{fmt(e.tien_mat_kiot)}</td>
                <td className={`p-2 text-right bg-orange-500/5 font-semibold ${chenhLechColor(e.tien_mat_chenh_lech)}`}>
                  {fmtChenhLech(e.tien_mat_chenh_lech)}
                </td>
                <td className="p-2 text-right text-slate-300">{fmt(e.vnpay)}</td>
                <td className="p-2 text-right text-slate-300">{fmt(e.momo_diem)}</td>
                <td className="p-2 text-right text-slate-300">{fmt(e.grab)}</td>
                <td className="p-2 text-right text-slate-300">{fmt(e.quet_the)}</td>
                <td className="p-2 text-right text-slate-200 bg-emerald-500/5">{fmt(e.ck_so)}</td>
                <td className="p-2 text-right text-slate-200 bg-emerald-500/5">{fmt(e.ck_kiot)}</td>
                <td className={`p-2 text-right bg-emerald-500/5 font-semibold ${chenhLechColor(e.ck_chenh_lech)}`}>
                  {fmtChenhLech(e.ck_chenh_lech)}
                </td>
                <td className="p-2 text-red-400/80 text-[10px] whitespace-pre-wrap">{e.ghi_chu_hd}</td>
                <td className="p-2 text-slate-500 text-[10px] whitespace-pre-wrap">{e.ghi_chu}</td>
                <td className="p-2 text-right">
                  <button
                    onClick={() => onDelete(e.id, e.tai_khoan, e.date)}
                    className="text-slate-500 hover:text-red-400 p-1"
                    data-testid={`dtn-delete-${e.id}`}
                    title="Xoá dòng"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </td>
              </tr>
            ))}

            {/* Subtotal row */}
            <tr className="bg-red-500/10 border-t-2 border-red-500/30 font-bold text-red-400">
              <td colSpan={2} className="p-2 text-right">Tổng ngày {formatDateVN(date)}:</td>
              <td className="p-2 text-right bg-blue-500/10">{fmt(subtotal.doanh_thu_so)}</td>
              <td className="p-2 text-right bg-blue-500/10">{fmt(subtotal.doanh_thu_kiot)}</td>
              <td className={`p-2 text-right bg-blue-500/10 ${chenhLechColor(subtotal.doanh_thu_chenh_lech)}`}>
                {fmtChenhLech(subtotal.doanh_thu_chenh_lech)}
              </td>
              <td className="p-2 text-right bg-orange-500/10">{fmt(subtotal.tien_mat_so)}</td>
              <td className="p-2 text-right bg-orange-500/10">{fmt(subtotal.tien_mat_kiot)}</td>
              <td className={`p-2 text-right bg-orange-500/10 ${chenhLechColor(subtotal.tien_mat_chenh_lech)}`}>
                {fmtChenhLech(subtotal.tien_mat_chenh_lech)}
              </td>
              <td className="p-2 text-right">{fmt(subtotal.vnpay)}</td>
              <td className="p-2 text-right">{fmt(subtotal.momo_diem)}</td>
              <td className="p-2 text-right">{fmt(subtotal.grab)}</td>
              <td className="p-2 text-right">{fmt(subtotal.quet_the)}</td>
              <td className="p-2 text-right bg-emerald-500/10">{fmt(subtotal.ck_so)}</td>
              <td className="p-2 text-right bg-emerald-500/10">{fmt(subtotal.ck_kiot)}</td>
              <td className={`p-2 text-right bg-emerald-500/10 ${chenhLechColor(subtotal.ck_chenh_lech)}`}>
                {fmtChenhLech(subtotal.ck_chenh_lech)}
              </td>
              <td colSpan={3}></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
